import { normalizeRole } from "./access";

const AUTH_USER_KEY = "lfx_auth_user";
const AUTH_USERS_KEY = "lfx_auth_users";
const AUTH_PASSKEYS_KEY = "lfx_auth_passkeys";
const AUTH_PROFILE_OVERRIDES_KEY = "lfx_auth_profile_overrides";

const DEMO_USERS = [
  { email: "vendor@nistta.com", password: "vendor123", role: "vendor", name: "Vendor User" },
  { email: "partner@nistta.com", password: "partner123", role: "partner", name: "Partner User" },
  { email: "shopper@nistta.com", password: "shopper123", role: "shopper", name: "Shopper User" },
  { email: "customer@nistta.com", password: "customer123", role: "customer", name: "Customer User" },
];

const getRegisteredUsers = () => {
  const raw = safeReadStorage(AUTH_USERS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const setRegisteredUsers = (users) => {
  safeWriteStorage(AUTH_USERS_KEY, JSON.stringify(users || []));
};

const getPasskeyEntries = () => {
  const raw = safeReadStorage(AUTH_PASSKEYS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const setPasskeyEntries = (entries) => {
  safeWriteStorage(AUTH_PASSKEYS_KEY, JSON.stringify(entries || []));
};

const getProfileOverrides = () => {
  const raw = safeReadStorage(AUTH_PROFILE_OVERRIDES_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const setProfileOverrides = (map) => {
  safeWriteStorage(AUTH_PROFILE_OVERRIDES_KEY, JSON.stringify(map || {}));
};

const applyProfileOverride = (user) => {
  if (!user || !user.email) return user;
  const key = String(user.email || "").trim().toLowerCase();
  const patch = getProfileOverrides()?.[key];
  if (!patch || typeof patch !== "object") return user;
  return { ...user, ...patch };
};

const toBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const fromBase64Url = (s) => {
  const input = String(s || "").replace(/-/g, "+").replace(/_/g, "/");
  const pad = input.length % 4 ? "=".repeat(4 - (input.length % 4)) : "";
  const b64 = input + pad;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};

const randomChallenge = () => crypto.getRandomValues(new Uint8Array(32));
const isWebAuthnAvailable = () =>
  typeof window !== "undefined"
  && !!window.PublicKeyCredential
  && !!navigator.credentials
  && typeof navigator.credentials.create === "function"
  && typeof navigator.credentials.get === "function"
  && window.isSecureContext;

const createSession = (user) => {
  const u = applyProfileOverride(user);
  const sessionUser = {
    email: u.email,
    name: u.name,
    role: normalizeRole(u.role),
    mobile: u.mobile,
    address: u.address,
  };
  safeWriteStorage(AUTH_USER_KEY, JSON.stringify(sessionUser));
  safeWriteStorage("lfx_user_role", sessionUser.role);
  return sessionUser;
};

const safeReadStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeWriteStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore storage failures for local/demo auth
  }
};

const safeRemoveStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // ignore storage failures for local/demo auth
  }
};

export const getAuthUser = () => {
  const raw = safeReadStorage(AUTH_USER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.role) return null;
    return applyProfileOverride({ ...parsed, role: normalizeRole(parsed.role) });
  } catch (e) {
    return null;
  }
};

export const getAccountProfile = (email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail) return null;
  const allUsers = [...DEMO_USERS, ...getRegisteredUsers()];
  const base = allUsers.find((u) => String(u.email || "").toLowerCase() === normalizedEmail);
  if (!base) return null;
  const merged = applyProfileOverride(base);
  return {
    email: merged.email,
    name: merged.name || "",
    role: normalizeRole(merged.role),
    mobile: merged.mobile || "",
    address: merged.address || "",
  };
};

export const updateAccountProfile = ({ email, name, mobile, address }) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const nextName = String(name || "").trim();
  const nextMobile = String(mobile || "").trim();
  const nextAddress = String(address || "").trim();
  if (!normalizedEmail || !nextName || !nextMobile || !nextAddress) {
    return { ok: false, error: "Name, mobile, and address are required." };
  }
  let updated = false;
  const regUsers = getRegisteredUsers();
  const nextRegUsers = regUsers.map((u) => {
    if (String(u.email || "").toLowerCase() !== normalizedEmail) return u;
    updated = true;
    return { ...u, name: nextName, mobile: nextMobile, address: nextAddress };
  });
  if (updated) {
    setRegisteredUsers(nextRegUsers);
  } else {
    const demoExists = DEMO_USERS.some((u) => String(u.email || "").toLowerCase() === normalizedEmail);
    if (!demoExists) return { ok: false, error: "Account not found." };
    const overrides = getProfileOverrides();
    overrides[normalizedEmail] = {
      ...(overrides[normalizedEmail] || {}),
      name: nextName,
      mobile: nextMobile,
      address: nextAddress,
    };
    setProfileOverrides(overrides);
  }
  const authUser = getAuthUser();
  if (authUser && String(authUser.email || "").toLowerCase() === normalizedEmail) {
    safeWriteStorage(AUTH_USER_KEY, JSON.stringify({
      ...authUser,
      name: nextName,
      mobile: nextMobile,
      address: nextAddress,
    }));
  }
  return { ok: true };
};

export const isAuthenticated = () => !!getAuthUser();

export const login = ({ email, password }) => {
  const allUsers = [...DEMO_USERS, ...getRegisteredUsers()];
  const user = allUsers.find(
    (u) => u.email.toLowerCase() === String(email || "").trim().toLowerCase() && u.password === password
  );
  if (!user) {
    return { ok: false, error: "Invalid email or password" };
  }
  const sessionUser = createSession(user);
  return { ok: true, user: sessionUser };
};

export const registerPasskeyForEmail = async ({ email, name }) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail) return { ok: false, error: "Email is required to enable fingerprint login." };
  if (!isWebAuthnAvailable()) {
    return { ok: false, error: "Fingerprint/Passkey is not supported on this device or browser." };
  }
  try {
    const userId = new TextEncoder().encode(normalizedEmail);
    const publicKey = {
      challenge: randomChallenge(),
      rp: { name: "NISTTA" },
      user: {
        id: userId,
        name: normalizedEmail,
        displayName: String(name || normalizedEmail).trim() || normalizedEmail,
      },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -257 },
      ],
      timeout: 60000,
      attestation: "none",
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        residentKey: "required",
        userVerification: "required",
      },
    };
    const credential = await navigator.credentials.create({ publicKey });
    if (!credential || !credential.rawId) {
      return { ok: false, error: "Could not register fingerprint credential." };
    }
    const credentialId = toBase64Url(credential.rawId);
    const entries = getPasskeyEntries().filter((x) => String(x?.email || "").toLowerCase() !== normalizedEmail);
    entries.push({ email: normalizedEmail, credentialId });
    setPasskeyEntries(entries);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.message || "Fingerprint setup was cancelled or failed." };
  }
};

export const removePasskeyForEmail = (email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail) return { ok: false, error: "Email is required." };
  const current = getPasskeyEntries();
  const next = current.filter((x) => String(x?.email || "").toLowerCase() !== normalizedEmail);
  setPasskeyEntries(next);
  return { ok: true };
};

export const loginWithPasskey = async ({ email }) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!isWebAuthnAvailable()) {
    return { ok: false, error: "Fingerprint/Passkey is not supported on this device or browser." };
  }
  const allEntries = getPasskeyEntries();
  const entry = normalizedEmail
    ? allEntries.find((x) => String(x?.email || "").toLowerCase() === normalizedEmail)
    : null;
  if (normalizedEmail && !entry?.credentialId) {
    return { ok: false, error: "No fingerprint/passkey found for this account." };
  }
  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: randomChallenge(),
        timeout: 60000,
        userVerification: "required",
        ...(entry?.credentialId
          ? { allowCredentials: [{ type: "public-key", id: fromBase64Url(entry.credentialId) }] }
          : {}),
      },
    });
    const resolvedEmail = (() => {
      if (normalizedEmail) return normalizedEmail;
      const rawId = assertion?.rawId ? toBase64Url(assertion.rawId) : "";
      const hit = allEntries.find((x) => String(x?.credentialId || "").trim() === rawId);
      return String(hit?.email || "").toLowerCase();
    })();
    if (!resolvedEmail) return { ok: false, error: "Passkey recognized, but no linked account was found." };
    const allUsers = [...DEMO_USERS, ...getRegisteredUsers()];
    const user = allUsers.find((u) => String(u.email || "").toLowerCase() === resolvedEmail);
    if (!user) return { ok: false, error: "Account no longer exists for this passkey." };
    const sessionUser = createSession(user);
    return { ok: true, user: sessionUser };
  } catch (e) {
    return { ok: false, error: e?.message || "Fingerprint verification failed." };
  }
};

export const hasPasskeyForEmail = (email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail) return false;
  return getPasskeyEntries().some((x) => String(x?.email || "").toLowerCase() === normalizedEmail);
};

export const signup = ({ email, password, name, role = "shopper", mobile, address }) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const normalizedName = String(name || "").trim();
  const normalizedMobile = String(mobile || "").trim();
  const normalizedAddress = String(address || "").trim();
  const normalizedRole = normalizeRole(role);
  if (!normalizedEmail || !password || !normalizedName || !normalizedMobile || !normalizedAddress) {
    return { ok: false, error: "Please fill all required fields." };
  }
  const allUsers = [...DEMO_USERS, ...getRegisteredUsers()];
  if (allUsers.some((u) => String(u.email || "").toLowerCase() === normalizedEmail)) {
    return { ok: false, error: "An account already exists with this email." };
  }
  const regUsers = getRegisteredUsers();
  const nextUser = {
    email: normalizedEmail,
    password: String(password),
    role: normalizedRole,
    name: normalizedName,
    mobile: normalizedMobile,
    address: normalizedAddress,
  };
  setRegisteredUsers([...regUsers, nextUser]);

  const sessionUser = createSession(nextUser);
  return { ok: true, user: sessionUser };
};

export const logout = () => {
  safeRemoveStorage(AUTH_USER_KEY);
  safeRemoveStorage("lfx_user_role");
};

export const getDefaultRouteForRole = (role) => {
  const normalized = normalizeRole(role);
  if (normalized === "vendor") return "/adminProducts";
  try {
    const qs = new URLSearchParams(window.location.search || "");
    const home = String(qs.get("home") || "").trim().toLowerCase();
    if (home === "old" || home === "products") return "/products";
    if (home === "new" || home === "landing") return "/landing";
  } catch (e) {
    // ignore query parse issues and use default
  }
  return "/landing";
};

export const getCurrentUserRole = () => {
  const authUser = getAuthUser();
  if (authUser?.role) return normalizeRole(authUser.role);

  try {
    const fromUrl = new URLSearchParams(window.location.search).get("role");
    if (fromUrl) {
      const normalized = normalizeRole(fromUrl);
      safeWriteStorage("lfx_user_role", normalized);
      return normalized;
    }
  } catch (e) {
    // Ignore URL parsing/storage issues and continue with fallbacks.
  }

  try {
    const fromStorage = safeReadStorage("lfx_user_role");
    if (fromStorage) return normalizeRole(fromStorage);
  } catch (e) {
    // Ignore storage errors and continue.
  }

  return "shopper";
};